import { memo, useEffect, useMemo, useState, Fragment } from "react";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import { useField } from "formik";
import classNames from "classnames";
import { messagePropType } from "app/utils/propTypes";
import {
	FloatingPortal,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useTransitionStyles,
} from "@floating-ui/react";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AdvancedSelectFormik from "app/pages/.shared/form/AdvancedSelectFormik/AdvancedSelectFormik";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import { InputCore } from "app/components/form/InputCore/InputCore";
import { FieldLabel } from "app/components/form/Field/Field";
import "./FloatingAdvancedSelectFormik.scss";

const FloatingAdvancedSelectFormik = props => {
	const {
		id,
		label,
		className,
		isRequired,
		getOptionLabel = () => {},
		floatingConfig = {},
		isSearchable = true,
		selectRef = {},
		loading,
		usePortalForMenu, // useful for search form above an overlay in sdp listing
		name,
	} = props;

	const [field, meta] = useField(name);

	const [showFloatingModal, setFloatingModal] = useState(false);

	// Drives floated-label state off the field's actual value, not Formik's touched state — the
	// previous computation left the label floated after a touched field's value was cleared (pre-existing bug).
	const hasValue = Boolean(field.value);

	const { x, y, refs, strategy, context } = useFloating({
		placement: floatingConfig.placement,
		strategy: "absolute",
		open: showFloatingModal,
		onOpenChange: setFloatingModal,
		middleware: [offset(floatingConfig.offset), shift()],
	});
	const dismiss = useDismiss(context, { ancestorScroll: false });
	const click = useClick(context, {
		event: "mousedown",
		toggle: false,
		keyboardHandlers: false,
	});
	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
	const { styles: transitionStyles, isMounted } = useTransitionStyles(context, {
		duration: 250,
		initial: {
			opacity: 0,
			transform: "scale(0.95)",
		},
		open: {
			opacity: 1,
			transform: "scale(1)",
		},
		close: {
			opacity: 0,
			transform: "scale(0.95)",
		},
	});

	const containerClassName = useMemo(
		() =>
			classNames("floating-advanced-select-container", className, {
				"floating-advanced-select-container--required": isRequired,
				"floating-advanced-select-container--touched": hasValue,
				"floating-advanced-select-container--error ": meta.touched && meta.error,
				"floating-advanced-select-container--focus": showFloatingModal,
			}),
		[className, isRequired, hasValue, meta.touched, meta.error]
	);

	useEffect(() => {
		if (!isEmpty(field.value)) {
			setFloatingModal(false);
		}
	}, [field.value]);

	const PortalOrFragment = usePortalForMenu ? FloatingPortal : Fragment;

	return (
		<div
			className={containerClassName}
			ref={refs.setReference}
			{...getReferenceProps({})}
			data-testid={props["data-testid"]}
		>
			{!loading ? (
				<InputCore
					ref={selectRef}
					invalid={Boolean(meta.touched && meta.error)}
					data-floated={hasValue || undefined}
					className="floating-advanced-select__core"
				>
					{field.value && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							className="floating-advanced-select__value"
						>
							{getOptionLabel(field.value)}
						</Typography>
					)}
					<FieldLabel
						htmlFor={id}
						error={Boolean(meta.touched && meta.error)}
						required={isRequired}
						data-floated={hasValue || undefined}
						className="floating-advanced-select__label"
					>
						<Typography
							variant={
								hasValue ? TYPOGRAPHY_VARIANTS.XSMALL : TYPOGRAPHY_VARIANTS.REGULAR
							}
							data-testid={`${props["data-testid"]}-label`}
						>
							{label}
						</Typography>
					</FieldLabel>
				</InputCore>
			) : (
				<div className="floating-advanced-select__loader">
					<LoaderBar height={10} width={"80%"} />
					<LoaderBar height={10} />
				</div>
			)}

			{showFloatingModal && isMounted && (
				<PortalOrFragment>
					<div
						className={classNames("floating-advanced-select__modal", {
							"floating-advanced-select__modal--touched": hasValue,
						})}
						data-testid={
							props["data-testid"] ? `${props["data-testid"]}-floating` : undefined
						}
						ref={refs.setFloating}
						{...getFloatingProps({
							style: {
								position: strategy,
								left: x,
								top: y,
								zIndex: 10,
								...transitionStyles,
							},
						})}
					>
						<AdvancedSelectFormik
							menuIsOpen={showFloatingModal}
							autoFocus={isSearchable}
							isClearable={isSearchable}
							label={label}
							classNamePrefix="advanced-select__select-sdp"
							menuPortalTarget={null}
							{...omit(props, ["selectRef", "className"])} // selectRef is used on the trigger's InputCore.
							onChange={value => {
								props.onChange(value);
								if (Boolean(value) && !isEmpty(value)) {
									setFloatingModal(false);
								}
							}}
						/>
					</div>
				</PortalOrFragment>
			)}
		</div>
	);
};

FloatingAdvancedSelectFormik.propTypes = {
	id: PropTypes.string,
	label: messagePropType,
	getOptionLabel: PropTypes.func,
	getOptionValue: PropTypes.func,
	onChange: PropTypes.func,
	options: PropTypes.array,
	onFocus: PropTypes.func,
	isRequired: PropTypes.bool,
	captureMenuScroll: PropTypes.bool,
	menuShouldBlockScroll: PropTypes.bool,
	loading: PropTypes.bool,
	usePortalForMenu: PropTypes.bool,
	name: PropTypes.string,
	floatingConfig: PropTypes.object,
	isSearchable: PropTypes.bool,
	selectRef: PropTypes.object,
	placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	"data-testid": PropTypes.string,
};

export default memo(FloatingAdvancedSelectFormik);
